import { useRef, useState } from "react";
import { Trans } from "@lingui/macro";
import { CardQuestion, CardActions } from "./CardPrimitives";
import { useIntakeMobileChromeRegistration } from "../IntakeMobileChromeContext";
import type { CardDef, UploadCategory } from "../types";
import type { IntakeData } from "@/lib/types";
import { Upload, X, FileText } from "lucide-react";

interface FileUploadCardProps {
  card: CardDef;
  uploads: IntakeData["uploads"];
  onUploads: (uploads: IntakeData["uploads"]) => void;
  onNext: () => void;
  onBack: () => void;
  onSkip?: () => void;
  index: number;
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function FileUploadCard({
  card,
  uploads,
  onUploads,
  onNext,
  onBack,
  onSkip,
  index,
}: FileUploadCardProps) {
  const category = card.uploadCategory as UploadCategory;
  const files = (uploads[category] ?? []).filter(
    (f): f is File => f instanceof File,
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const addFiles = (newFiles: File[]) => {
    const merged = [...files, ...newFiles];
    onUploads({ ...uploads, [category]: merged });
  };

  const removeFile = (idx: number) => {
    const updated = files.filter((_, i) => i !== idx);
    onUploads({ ...uploads, [category]: updated });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length) addFiles(dropped);
  };

  useIntakeMobileChromeRegistration(
    () => ({
      onContinue: onNext,
      onCancel: onBack,
      onSkip,
    }),
    [onNext, onBack, onSkip],
  );

  return (
    <div>
      <CardQuestion question={card.question} hint={card.hint} index={index} />

      {/* Drop zone */}
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={`max-w-lg border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all duration-150 ${
          isDragging
            ? "border-[#166B93] bg-[#166B93]/5"
            : "border-gray-300 hover:border-[#166B93]/50 hover:bg-gray-50"
        }`}
      >
        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
        <p className="text-sm font-medium text-gray-600">
          <Trans>Drag & drop files here, or</Trans>{" "}
          <span className="text-[#166B93] underline">
            <Trans>browse</Trans>
          </span>
        </p>
        <p className="text-xs text-gray-400 mt-1">
          <Trans>PDF, JPG, PNG, DICOM accepted</Trans>
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          accept=".pdf,.jpg,.jpeg,.png,.dcm,.tif,.tiff"
          onChange={(e) => {
            const picked = Array.from(e.target.files ?? []);
            if (picked.length) addFiles(picked);
            e.target.value = "";
          }}
        />
      </div>

      {/* File list */}
      {files.length > 0 && (
        <ul className="mt-4 max-w-lg space-y-2">
          {files.map((f, i) => (
            <li
              key={i}
              className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl border border-gray-200"
            >
              <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="flex-1 text-sm text-gray-700 truncate">{f.name}</span>
              <span className="text-xs text-gray-400">{formatSize(f.size)}</span>
              <button
                type="button"
                onClick={() => removeFile(i)}
                className="text-gray-400 hover:text-red-500 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <CardActions
        onNext={onNext}
        onBack={onBack}
        onSkip={onSkip}
      />
    </div>
  );
}
